import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import InventoryIcon from '@mui/icons-material/Inventory';
import MapIcon from '@mui/icons-material/Map';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import SellIcon from '@mui/icons-material/Sell';
import Settings from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TaskIcon from '@mui/icons-material/Task';
import CloseIcon from '@mui/icons-material/Close';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import {
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Fab,
  CircularProgress,
  Button,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import { default as Drawer, default as MuiDrawer } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { camelCase, get, mapKeys } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import axios, { apiQuiet } from '../../axios';
import LoadingPage from '../../components/LoadingPage';
import { useAuth } from '../../context/AuthContext';
import { useSystemSettings } from '../../context/SystemSettingsContext';
import { Organization } from '../../models/organization.model';
import doesRoleExist from '../../utils/doesRoleExist';
import AccountMenu from './AccountMenu';
import CreateOrganizationPage from './CreateOrganizationPage/CreateOrganizationPage';
import CustomerDetailsPage from './CustomersPage/CustomerDetailsPage';
import CustomersPage from './CustomersPage/CustomersPage';
import MapPage from './MapPage/MapPage';
import NotFoundPage from './NotFoundPage/NotFoundPage';
import OrderDetailsPage from './OrdersPage/OrderDetailsPage';
import OrdersPage from './OrdersPage/OrdersPage';
import OverviewPage from './Overview/OverviewPage';
import ProductDetailsPage from './ProductsPage/ProductDetailsPage';
import ProductsPage from './ProductsPage/ProductsPage';
import ReportsPage from './ReportsPage/ReportsPage';
import SettingsPage from './SettingsPage/SettingsPage';
import SubscriptionChangeDialog from './SettingsPage/SubscriptionChangeDialog';
import TaskDetailsPage from './TasksPage/TaskDetailsPage';
import TasksPage from './TasksPage/TasksPage';
import TeamsPage from './TeamsPage/TeamsPage';
import UsersPage from './UsersPage/UsersPage';
import LocationTracker from '../../components/LocationTracker';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS, de, fr, nl } from 'date-fns/locale';

const drawerWidth = 240;

const pageNameMap = {
  '/app/overview': 'PAGE_NAME.OVERVIEW',
  '/app/users': 'PAGE_NAME.USERS',
  '/app/teams': 'PAGE_NAME.TEAMS',
  '/app/reports': 'PAGE_NAME.REPORTS',
  '/app/settings': 'PAGE_NAME.SETTINGS',
  '/app/customers': 'PAGE_NAME.CUSTOMERS',
  '/app/tasks': 'PAGE_NAME.TASKS',
  '/app/orders': 'PAGE_NAME.ORDERS',
  '/app/products': 'PAGE_NAME.PRODUCTS',
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export default function ProfilePage() {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscriptionChangeOpen, setIsSubscriptionChangeOpen] =
    useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const { path } = useRouteMatch();
  const {
    setIndustries,
    setRoles,
    setCustomerTypes,
    setCustomerActivityTypes,
    setCustomerActivityChannels,
    setTaskPriorityTypes,
    setTaskStatusTypes,
    setOrderStatusTypes,
  } = useSystemSettings();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const {
    setUser,
    user,
    organization,
    setOrganizations,
    setOrganization,
    subscription,
    setSubscription,
  } = useAuth();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);
  const [isChatBotOnline, setIsChatBotOnline] = useState<boolean | null>(null);
  // Sticky: once the bot iframe is up we never take it down again for the rest
  // of the session. See the comment on the effect below.
  const [hasLoadedChatBot, setHasLoadedChatBot] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const chatBotIframeRef = useRef<HTMLIFrameElement | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Probe the bot only while it is not yet running.
  //
  // WHY THIS MUST NOT TEAR THE IFRAME DOWN
  // The salesbot iframe owns its requests to n8n, which routinely take tens of
  // seconds. Unmounting the iframe destroys its browsing context: the pending
  // fetch is aborted and the answer is lost for good, so the user reopens the
  // panel to find their own question and no reply. The Drawer already sets
  // keepMounted, so leaving the iframe rendered is enough -- a hidden iframe
  // keeps running and its answer still lands in the transcript.
  useEffect(() => {
    if (!isChatBotOpen || hasLoadedChatBot) return;

    let active = true;
    setIsChatBotOnline(null);
    const salesBotUrl = process.env.REACT_APP_SALESBOT_URL;
    const baseUrl = salesBotUrl || `https://${window.location.hostname}:5173`;

    const checkOnline = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 3000);

      try {
        await fetch(baseUrl, { mode: 'no-cors', signal: controller.signal });
        if (active) {
          setIsChatBotOnline(true);
          setHasLoadedChatBot(true);
        }
      } catch (err) {
        if (active) {
          setIsChatBotOnline(false);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkOnline();
    return () => {
      active = false;
    };
  }, [isChatBotOpen, retryCount, hasLoadedChatBot]);

  let defaultLanguage =
    i18n.language || window.localStorage.i18nextLng || 'en-US';
  if (
    !['tr-TR', 'en-US', 'de-DE', 'fr-FR', 'nl-NL'].includes(defaultLanguage)
  ) {
    defaultLanguage = 'en-US';
  }

  const [language, setLanguage] = React.useState(defaultLanguage);

  // The bot reads `lang` from its URL once, at boot. Keeping the boot value in
  // a ref stops a later language change from rewriting the iframe src, which
  // would reload the iframe and lose any answer still in flight. Live language
  // changes are pushed over postMessage instead -- see the effect below.
  const initialLanguageRef = useRef(language);

  const chatBotUrl = React.useMemo(() => {
    const salesBotUrl = process.env.REACT_APP_SALESBOT_URL;
    const baseUrl = salesBotUrl || `https://${window.location.hostname}:5173`;

    if (!user || !organization) return baseUrl;

    const role = user.organizationRoleMap?.find(
      (org: any) => org.organizationId === organization.id
    )?.role;

    const parentOrigin = encodeURIComponent(window.location.origin);
    return `${baseUrl}?role=${role}&organization_id=${
      organization.id
    }&user_id=${user.id}&parent_origin=${parentOrigin}&lang=${
      initialLanguageRef.current
    }&org_name=${encodeURIComponent(organization.name)}`;
  }, [user, organization]);

  // The origin the bot is served from. We use it two ways, and both matter:
  // as the target of every postMessage (never '*', which would broadcast to
  // whatever page happens to be embedded), and to check who is talking to us
  // before we answer.
  const chatBotOrigin = React.useMemo(() => {
    const salesBotUrl = process.env.REACT_APP_SALESBOT_URL;
    const baseUrl = salesBotUrl || `https://${window.location.hostname}:5173`;
    try {
      return new URL(baseUrl, window.location.origin).origin;
    } catch (err) {
      // A malformed REACT_APP_SALESBOT_URL should not break the page.
      return null;
    }
  }, []);

  // Tell the bot about a language change without reloading it.
  useEffect(() => {
    const contentWindow = chatBotIframeRef.current?.contentWindow;
    if (!contentWindow || !chatBotOrigin) return;

    contentWindow.postMessage(
      { type: 'ecosh:language', language },
      chatBotOrigin
    );
  }, [language, chatBotOrigin]);

  // ── Give the bot a signed token when it asks for one ─────────────────────
  //
  // WHY THE BOT CANNOT DO THIS ITSELF
  // The bot runs in an iframe, and anything inside a browser can be read by
  // whoever is using it. So it cannot hold a secret and cannot prove who the
  // user is. We can, because they signed in here. We ask our own backend for a
  // short-lived signed token and hand it over; the assistant's gateway checks
  // our signature before letting anything reach the AI or the database.
  //
  // WHY OVER postMessage AND NOT IN THE IFRAME URL
  // Addresses end up in browser history, server logs and Referer headers. And
  // the src must never be rewritten while the panel is open -- that reloads the
  // bot and throws away any answer still on its way. postMessage is the only
  // channel that can deliver a *second* token, so it delivers the first one too.
  //
  // The bot asks twice: 'tdg:ready' when it loads, and 'tdg:token-request'
  // shortly before its token expires or after the gateway refuses one.
  useEffect(() => {
    if (!chatBotOrigin || !organization) return;

    const handleBotMessage = async (event: MessageEvent) => {
      // Only the bot we embedded may ask us for a token.
      if (event.origin !== chatBotOrigin) return;

      const type = event.data?.type;
      if (type !== 'tdg:ready' && type !== 'tdg:token-request') return;

      try {
        // Use `apiQuiet`, NOT the default axios instance. The default one
        // force-redirects the whole window to the login screen on any 401 /
        // "Unauthenticated." response -- so a failed token mint would log the
        // user out just for opening the bot. apiQuiet has the same session and
        // CSRF handling but never redirects, so a failure stays contained here:
        // the bot simply opens without a token (permissive) and the user is
        // undisturbed.
        //
        // Refresh the CSRF cookie first. This is a state-changing POST behind
        // Sanctum's stateful guard, so it needs a current XSRF-TOKEN -- exactly
        // like login and sign-up do (see SignInPage). Skipping it gives a
        // "CSRF token mismatch" 500 when the cookie has gone stale.
        await apiQuiet('sanctum/csrf-cookie');

        const resp = await apiQuiet.post(
          `api/organizations/${organization.id}/bot/token`
        );

        const token = get(resp, 'data.data.token');
        const expiresAt = get(resp, 'data.data.expiresAt');
        if (!token) return;

        // Reply to the window that actually asked, rather than reading the ref:
        // the bot is by definition alive and listening at this moment.
        const source = event.source as Window | null;
        (source ?? chatBotIframeRef.current?.contentWindow)?.postMessage(
          { type: 'tdg:token', token, expiresAt },
          chatBotOrigin
        );
      } catch (err) {
        // A 503 means this environment has no assistant configured, which is
        // normal for local and staging. The bot carries on without a token and
        // the gateway accepts it while it is still in permissive mode -- so
        // there is nothing here worth interrupting the user about.
      }
    };

    window.addEventListener('message', handleBotMessage);
    return () => window.removeEventListener('message', handleBotMessage);
  }, [chatBotOrigin, organization]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value);
    i18n.changeLanguage(event.target.value);
    localStorage.setItem('lang', event.target.value);
  };

  const getDatePickerLocale = useCallback(() => {
    if (language === 'tr-TR') {
      return tr;
    }
    if (language === 'de-DE') {
      return de;
    }
    if (language === 'fr-FR') {
      return fr;
    }
    if (language === 'nl-NL') {
      return nl;
    }

    return enUS;
  }, [language]);

  const fetchSubscription = useCallback(
    async (id: string) => {
      setIsSubscriptionLoading(true);
      if (process.env.REACT_APP_SUBSCRIPTION) {
        setSubscription({
          status: 'active',
          product: process.env.REACT_APP_SUBSCRIPTION,
        });
      } else {
        const subscriptionsResp = await axios.get(
          `api/organizations/${id}/subscriptions`
        );
        setSubscription(
          mapKeys(subscriptionsResp.data, (_v, k) => camelCase(k))
        );
      }
      setIsSubscriptionLoading(false);
    },
    [setSubscription]
  );

  useEffect(() => {
    const fetchOrganizations = async () => {
      const organizationsResp = await axios.get('api/organizations');

      const organizations = organizationsResp.data.data.items.map(
        (item: Organization) => {
          const org = new Organization();
          const orgObj = mapKeys(item, (_v, k) => camelCase(k));
          return org.deserialize(orgObj);
        }
      );

      setOrganizations(organizations);

      const activeOrgs = organizations.filter(
        (i: Organization) => !i.pivot.deleted_at
      );

      if (activeOrgs.length > 0) {
        const selectedOrganizationId = localStorage.getItem(
          'selectedOrganization'
        );

        let selectedOrganization: Organization = organizations.find(
          (org: Organization) =>
            org.id.toString() === String(selectedOrganizationId)
        );

        if (!selectedOrganization || selectedOrganization.pivot.deleted_at) {
          selectedOrganization = activeOrgs[0];
        }

        setOrganization(selectedOrganization);
      }
      setIsLoading(false);
    };
    fetchOrganizations();
  }, [setOrganizations, setOrganization, history]);

  useEffect(() => {
    if (organization) {
      fetchSubscription(organization.id);
      localStorage.setItem('selectedOrganization', organization.id);
    }
  }, [organization, fetchSubscription]);

  useEffect(() => {
    const fetchSystemVariables = async () => {
      const industriesResp = await axios.get('api/system-configs/industries');
      setIndustries(industriesResp.data.data);
      const customerTypesResp = await axios.get(
        'api/system-configs/customer_types'
      );
      setCustomerTypes(Object.keys(customerTypesResp.data.data));

      const rolesResp = await axios.get('api/system-configs/roles');
      setRoles(rolesResp.data.data);

      const taskPriorityResp = await axios.get(
        'api/system-configs/task_priority'
      );
      setTaskPriorityTypes(Object.keys(taskPriorityResp.data.data));

      const taskStatusResp = await axios.get('api/system-configs/task_status');
      setTaskStatusTypes(Object.keys(taskStatusResp.data.data));

      const orderStatusResp = await axios.get(
        'api/system-configs/order_status'
      );
      setOrderStatusTypes(Object.keys(orderStatusResp.data.data));

      const customerActivityResp = await axios.get(
        'api/system-configs/customer_activity_types'
      );
      setCustomerActivityTypes(Object.keys(customerActivityResp.data.data));
      const customerActivityChannelResp = await axios.get(
        'api/system-configs/customer_activity_channels'
      );
      setCustomerActivityChannels(
        Object.keys(customerActivityChannelResp.data.data)
      );
    };

    fetchSystemVariables();
  }, [
    setIndustries,
    setCustomerTypes,
    setRoles,
    setCustomerActivityTypes,
    setCustomerActivityChannels,
    setTaskPriorityTypes,
    setTaskStatusTypes,
    setOrderStatusTypes,
  ]);

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleDrawerOpen = () => {
    if (isMobile) {
      handleMobileDrawerToggle();
    } else {
      setOpen(true);
    }
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const logout = async () => {
    setIsLoading(true);
    await axios.post('api/auth/logout');
    setUser(null);
    localStorage.removeItem('user');
    history.push('/');
  };

  const DrawerList = (
    <List>
      {doesRoleExist(user, organization, ['ADMIN', 'OBSERVER']) && [
        <ListItem
          key={'USERS.Overview'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="overviewLink"
          onClick={() => {
            history.push(`${path}/overview`);
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('USERS.OVERVIEW')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <DashboardIcon />
              </ListItemIcon>
            </Tooltip>

            <ListItemText
              primary={t('USERS.OVERVIEW')}
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
        <ListItem
          key={'USERS.LINK'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="usersLink"
          onClick={() => {
            history.push(`${path}/users`);
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('USERS.LINK')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={t('USERS.LINK')}
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
        <ListItem
          key={'ORGANIZATION.TEAMS.LINK'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="teamsLink"
          onClick={() => {
            history.push(`${path}/teams`);
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('ORGANIZATION.TEAMS.LINK')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <GroupsIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={t('ORGANIZATION.TEAMS.LINK')}
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
        <ListItem
          key={'PRODUCTS.LINK'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="productsLink"
          onClick={() => {
            history.push(`${path}/products`);
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('PRODUCTS.LINK')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <InventoryIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={t('PRODUCTS.LINK')}
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
      ]}
      <ListItem
        key={'ORDERS.LINK'}
        disablePadding
        sx={{ display: 'block' }}
        data-testid="tasksLink"
        onClick={() => {
          history.push(`${path}/orders`);
        }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <Tooltip title={t('ORDERS.LINK')} placement="right">
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open || mobileDrawerOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <SellIcon />
            </ListItemIcon>
          </Tooltip>
          <ListItemText
            primary={t('ORDERS.LINK')}
            sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem
        key={'CUSTOMERS.LINK'}
        disablePadding
        sx={{ display: 'block' }}
        data-testid="customersLink"
        onClick={() => {
          history.push(`${path}/customers`);
        }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <Tooltip title={t('CUSTOMERS.LINK')} placement="right">
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open || mobileDrawerOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <BusinessIcon />
            </ListItemIcon>
          </Tooltip>
          <ListItemText
            primary={t('CUSTOMERS.LINK')}
            sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem
        key={'TASKS.LINK'}
        disablePadding
        sx={{ display: 'block' }}
        data-testid="tasksLink"
        onClick={() => {
          history.push(`${path}/tasks`);
        }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <Tooltip title={t('TASKS.LINK')} placement="right">
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open || mobileDrawerOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <TaskIcon />
            </ListItemIcon>
          </Tooltip>
          <ListItemText
            primary={t('TASKS.LINK')}
            sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>

      {doesRoleExist(user, organization, [
        'ADMIN',
        'OBSERVER',
        'TEAM_MANAGER',
      ]) && [
        <ListItem
          key={'MAP_PAGE.TITLE'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="mapLink"
          onClick={() => {
            if (subscription?.product === 'ecosh-professional') {
              history.push(`${path}/map`);
            } else {
              setIsSubscriptionChangeOpen(true);
            }
          }}
        >
          <ListItemButton
            disabled={subscription?.product !== 'ecosh-professional'}
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('MAP_PAGE.TITLE')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <MapIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={
                <>
                  <span>{t('MAP_PAGE.TITLE')}</span>{' '}
                  <Chip label="Professional" size="small" />
                </>
              }
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
        <ListItem
          key={'REPORTS.TITLE'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="reportsLink"
          onClick={() => {
            if (subscription?.product !== 'ecosh-lite') {
              history.push(`${path}/reports`);
            } else {
              setIsSubscriptionChangeOpen(true);
            }
          }}
        >
          <ListItemButton
            disabled={subscription?.product === 'ecosh-lite'}
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('REPORTS.TITLE')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <BarChartIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={
                <>
                  <span>{t('REPORTS.TITLE')}</span>{' '}
                  <Chip size="small" label="Standard" />
                </>
              }
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>,
      ]}

      {doesRoleExist(user, organization, ['ADMIN']) && (
        <ListItem
          key={'SETTINGS.TITLE'}
          disablePadding
          sx={{ display: 'block' }}
          data-testid="settingsLink"
          onClick={() => {
            history.push(`${path}/settings`);
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || mobileDrawerOpen ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <Tooltip title={t('SETTINGS.TITLE')} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || mobileDrawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <Settings />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={t('SETTINGS.TITLE')}
              sx={{ opacity: open || mobileDrawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
      )}
    </List>
  );

  const MobileDrawerContent = organization && (
    <Box>
      <Toolbar />
      <Divider />
      {DrawerList}
    </Box>
  );

  const DrawerContent = organization && (
    <Box sx={{ width: 'auto' }}>
      <DrawerHeader>
        <IconButton onClick={handleDrawerClose} data-testid="drawerClose">
          {theme.direction === 'rtl' ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider />
      {DrawerList}
    </Box>
  );

  const getPageName = useCallback(() => {
    return t(get(pageNameMap, location.pathname));
  }, [t, location.pathname]);

  const getRedirectPathName = useCallback(() => {
    if (doesRoleExist(user, organization, ['ADMIN', 'OBSERVER'])) {
      return '/app/overview';
    }

    return '/app/orders';
  }, [user, organization]);

  const container =
    window !== undefined ? () => window.document.body : undefined;

  /* istanbul ignore next */
  const RedirectOrganizationRoute = useCallback(
    ({ children, ...rest }: any) => {
      return (
        <Route
          {...rest}
          render={({ location }) =>
            organization ? (
              children
            ) : (
              <Redirect
                to={{
                  pathname: '/app/createOrganization',
                  state: { from: location },
                }}
              />
            )
          }
        />
      );
    },
    [organization]
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getDatePickerLocale()}
    >
      <Box
        sx={{ display: 'flex', overflowX: 'auto' }}
        data-testid="profilePage"
      >
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            {organization && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                data-testid="drawerOpen"
                edge="start"
                sx={{
                  marginRight: 5,
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h6"
              color="inherit"
              component="div"
              data-testid="appLink"
              onClick={() => {
                history.push('/app');
              }}
              sx={{ cursor: 'pointer' }}
            >
              ECOSH
            </Typography>

            <Typography
              variant="subtitle1"
              color="inherit"
              component="div"
              sx={{ ml: 1, alignSelf: 'center' }}
            >
              {subscription?.product === 'ecosh-professional' && 'Professional'}
              {subscription?.product === 'ecosh-standard' && 'Standard'}
              {subscription?.product === 'ecosh-lite' && 'Lite'}
            </Typography>

            {subscription?.status === 'trial' && (
              <Chip
                sx={{ color: 'white', ml: 2 }}
                label={t('SUBSCRIPTION_FREE_TRIAL')}
              />
            )}

            <Box display="flex" mr={1} justifyContent="flex-end" width="100%">
              <FormControl
                variant="standard"
                sx={{ m: 1, width: 70, color: 'white' }}
              >
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={language}
                  onChange={handleLanguageChange}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                    ':before': { borderBottomColor: 'white' },
                    ':after': { borderBottomColor: 'white' },
                  }}
                >
                  <MenuItem value="en-US">EN</MenuItem>
                  <MenuItem value="tr-TR">TR</MenuItem>
                  <MenuItem value="de-DE">DE</MenuItem>
                  <MenuItem value="fr-FR">FR</MenuItem>
                  <MenuItem value="nl-NL">NL</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                ml: 'auto',
                mr: 0.5,
                width: 100,
                overflow: 'elipsis',
                textAlign: 'right',
              }}
              display="flex"
              flexDirection="column"
            >
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: 100,
                }}
                variant="caption"
              >
                {organization?.name}
              </Typography>
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: 100,
                }}
                variant="caption"
              >
                {user?.email}
              </Typography>
            </Box>
            <AccountMenu logOutHandler={logout} />
          </Toolbar>
        </AppBar>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {MobileDrawerContent}
        </Drawer>
        <DesktopDrawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {DrawerContent}
        </DesktopDrawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: '100%',
            marginTop: '64px',
          }}
        >
          <Typography
            variant="h5"
            mb={1.5}
            sx={{ color: 'darkgray' }}
            data-testid="pageName"
          >
            {getPageName()}
          </Typography>

          <Switch>
            <Route exact path="/app">
              <Redirect
                to={{
                  pathname: getRedirectPathName(),
                }}
              />
            </Route>
            <RedirectOrganizationRoute path={`${path}/customers`}>
              <CustomersPage />
            </RedirectOrganizationRoute>
            <RedirectOrganizationRoute path={`${path}/orders`}>
              <OrdersPage />
            </RedirectOrganizationRoute>
            <RedirectOrganizationRoute path={`${path}/order/:orderId`}>
              <OrderDetailsPage />
            </RedirectOrganizationRoute>
            <RedirectOrganizationRoute path={`${path}/customer/:customerId`}>
              <CustomerDetailsPage />
            </RedirectOrganizationRoute>
            <RedirectOrganizationRoute path={`${path}/tasks`}>
              <TasksPage />
            </RedirectOrganizationRoute>
            <RedirectOrganizationRoute path={`${path}/task/:taskId`}>
              <TaskDetailsPage />
            </RedirectOrganizationRoute>
            {doesRoleExist(user, organization, [
              'ADMIN',
              'OBSERVER',
              'TEAM_MANAGER',
            ]) && [
              subscription?.product === 'ecosh-professional' && (
                <RedirectOrganizationRoute
                  key={`${path}/map`}
                  path={`${path}/map`}
                >
                  <MapPage />
                </RedirectOrganizationRoute>
              ),
              subscription?.product !== 'ecosh-lite' && (
                <RedirectOrganizationRoute
                  key={`${path}/reports`}
                  path={`${path}/reports`}
                >
                  <ReportsPage />
                </RedirectOrganizationRoute>
              ),
            ]}

            {doesRoleExist(user, organization, ['ADMIN', 'OBSERVER']) && [
              <RedirectOrganizationRoute
                path={`${path}/products`}
                key={`${path}/products`}
              >
                <ProductsPage />
              </RedirectOrganizationRoute>,
              <RedirectOrganizationRoute
                path={`${path}/product/:productId`}
                key={`${path}/product/:productId`}
              >
                <ProductDetailsPage />
              </RedirectOrganizationRoute>,
              <RedirectOrganizationRoute
                path={`${path}/overview`}
                key={`${path}/overview`}
              >
                <OverviewPage />
              </RedirectOrganizationRoute>,
              <RedirectOrganizationRoute
                path={`${path}/teams`}
                key={`${path}/teams`}
              >
                <TeamsPage />
              </RedirectOrganizationRoute>,
              <RedirectOrganizationRoute
                path={`${path}/users`}
                key={`${path}/users`}
              >
                <UsersPage />
              </RedirectOrganizationRoute>,
              <RedirectOrganizationRoute
                path={`${path}/settings`}
                key={`${path}/settings`}
              >
                <SettingsPage />
              </RedirectOrganizationRoute>,
            ]}
            <Route
              path={`${path}/createOrganization`}
              key={`${path}/createOrganization`}
            >
              <CreateOrganizationPage />
            </Route>
            <Route path="/">
              <NotFoundPage />
            </Route>
          </Switch>
        </Box>
        <SubscriptionChangeDialog
          open={
            isSubscriptionChangeOpen ||
            (!['active', 'trial', 'canceled'].includes(subscription?.status) &&
              !isLoading &&
              !isSubscriptionLoading)
          }
          handleClose={() => setIsSubscriptionChangeOpen(false)}
        />
        <LocationTracker />
        <Tooltip title="Ask Ecosh AI" placement="left">
          <Fab
            color="primary"
            aria-label="chat"
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: (theme) => theme.zIndex.drawer - 50,
              background: (theme) =>
                `linear-gradient(45deg, ${theme.palette.primary.main} 30%, #ff8e53 90%)`,
              boxShadow: '0 3px 5px 2px rgba(233, 110, 6, .3)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1) rotate(5deg)',
                boxShadow: '0 6px 10px 4px rgba(233, 110, 6, .4)',
              },
            }}
            onClick={() => {
              setIsChatBotOpen(true);
            }}
          >
            <SmartToyIcon />
          </Fab>
        </Tooltip>
        <Drawer
          anchor="right"
          open={isChatBotOpen}
          onClose={() => setIsChatBotOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.tooltip + 1000,
            '& .MuiDrawer-paper': {
              width: 400,
              height: '100vh',
              maxHeight: '100vh',
              bottom: 0,
              right: 0,
              top: 0,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
              borderRadius: '0px',
              overflow: 'hidden',
              backgroundColor: '#fff',
              border: '1px solid rgba(0,0,0,0.05)',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <IconButton
              onClick={() => setIsChatBotOpen(false)}
              size="small"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 2500,
                color: 'var(--text-muted)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  color: 'var(--text-main)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1, position: 'relative', height: '100%' }}>
              {isChatBotOnline === null && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                    zIndex: 2000,
                  }}
                >
                  <CircularProgress
                    size={40}
                    sx={{ mb: 2, color: 'primary.main' }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t('SALES_BOT.CONNECTING')}
                  </Typography>
                </Box>
              )}

              {isChatBotOnline === false && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    zIndex: 2000,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <CloudOffIcon sx={{ fontSize: 40, color: '#d32f2f' }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#2d3748',
                      mb: 1.5,
                      fontFamily: '"Inter", "Roboto", sans-serif',
                    }}
                  >
                    {t('SALES_BOT.CONNECTION_FAILED')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#718096',
                      mb: 4,
                      lineHeight: 1.6,
                      maxWidth: 280,
                      fontFamily: '"Inter", "Roboto", sans-serif',
                    }}
                  >
                    {t('SALES_BOT.CONNECTION_FAILED_DESCRIPTION')}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setHasLoadedChatBot(false);
                      setRetryCount((prev) => prev + 1);
                    }}
                    sx={{
                      px: 4,
                      py: 1.2,
                      borderRadius: '24px',
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(233, 110, 6, 0.2)',
                      background:
                        'linear-gradient(45deg, #e96e06 30%, #ff8e53 90%)',
                      color: 'white',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(233, 110, 6, 0.35)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    {t('SALES_BOT.RETRY')}
                  </Button>
                </Box>
              )}

              {(isChatBotOnline === true || hasLoadedChatBot) && (
                <iframe
                  ref={chatBotIframeRef}
                  src={chatBotUrl}
                  title="Ecosh Salesbot"
                  scrolling="no"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              )}
            </Box>
          </Box>
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
}
